import React, { useState, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import useBenefitApplicationApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';

const Container = styled.div``;

const Segmented = styled.div`
  position: relative;
  display: inline-flex;
  align-items: stretch;
  padding: 4px;
  background: #EEF2F6;
  border-radius: 999px;
  margin-bottom: 16px;
  font-family: 'Nunito Sans', sans-serif;
  user-select: none;
`;

const SegmentedThumb = styled.span`
  position: absolute;
  top: 4px;
  bottom: 4px;
  width: ${({ $width }) => $width}px;
  transform: translateX(${({ $offset }) => $offset}px);
  background: #fff;
  border-radius: 999px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: transform 220ms cubic-bezier(0.32, 0.72, 0, 1), width 220ms cubic-bezier(0.32, 0.72, 0, 1);
  pointer-events: none;
`;

const SegmentedOption = styled.button`
  position: relative;
  z-index: 1;
  border: none;
  background: transparent;
  padding: 8px 18px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $active, theme }) => ($active ? theme.colors.text.primary : '#64748b')};
  cursor: pointer;
  transition: color 180ms ease;
  white-space: nowrap;
  &:hover { color: ${({ $active, theme }) => ($active ? theme.colors.text.primary : theme.colors.primary.main)}; }
  &:focus { outline: none; }
`;

// Panel kartı ?mode=pending ile gelir. Parametre yoksa bugünkü 'all' davranışı KORUNUR —
// menüden gelen kullanıcı için hiçbir şey değişmez.
export function resolveInitialMode(search) {
  return new URLSearchParams(search || '').get('mode') === 'pending' ? 'pending' : 'all';
}

export default function BenefitApplicationsPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const [mode, setMode] = useState(() => resolveInitialMode(location.search)); // 'all' | 'pending'
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const segmentedOptions = useMemo(() => ([
    { key: 'all', label: t('benefitApplication.tabAll') },
    { key: 'pending', label: t('benefitApplication.tabPending') },
  ]), [t]);

  const optionRefs = useRef({});
  const [thumbStyle, setThumbStyle] = useState({ width: 0, offset: 0 });

  useLayoutEffect(() => {
    const el = optionRefs.current[mode];
    if (!el) return;
    setThumbStyle({ width: el.offsetWidth, offset: el.offsetLeft - 4 });
  }, [mode, segmentedOptions]);

  const { columns: baseColumns, data, fetching, pagination, approve, reject } =
    useBenefitApplicationApi(queryParams, mode);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleApprove = useCallback(async (row) => {
    await approve(row.id);
  }, [approve]);

  const handleReject = useCallback(async (row) => {
    const reason = window.prompt(t('benefitApplication.rejectReasonPrompt'));
    if (reason && reason.trim()) {
      await reject(row.id, reason.trim());
    }
  }, [reject, t]);

  const handleDetails = useCallback((row) => {
    history.push(`/benefit-applications/${row.id}`);
  }, [history]);

  const actionOptions = useMemo(() => (row) => {
    const options = [
      { type: 'details', label: t('benefitApplication.details'), onClick: handleDetails },
    ];
    if (row?.canApprove) {
      options.unshift({ type: 'divider' });
      options.unshift({ type: 'reject', label: t('benefitApplication.reject'), onClick: handleReject });
      options.unshift({ type: 'approve', label: t('benefitApplication.approve'), onClick: handleApprove });
    }
    return options;
  }, [t, handleApprove, handleReject, handleDetails]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <OptionsMenu actions={actionOptions(row)} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data]);

  const handleModeChange = useCallback((next) => {
    if (next === mode) return;
    setMode(next);
    setQueryParams({ pageSize: 10, pageNumber: 1 });
    setFilterList([]);
  }, [mode]);

  return (
    <Container>
      <Segmented role="tablist" aria-label={t('benefitApplication.title')}>
        <SegmentedThumb $width={thumbStyle.width} $offset={thumbStyle.offset} />
        {segmentedOptions.map((opt) => (
          <SegmentedOption
            key={opt.key}
            ref={(el) => { optionRefs.current[opt.key] = el; }}
            $active={mode === opt.key}
            role="tab"
            aria-selected={mode === opt.key}
            onClick={() => handleModeChange(opt.key)}
          >
            {opt.label}
          </SegmentedOption>
        ))}
      </Segmented>

      {useMemo(() => (
        <DataTable
          title={t('benefitApplication.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleDetails}
        />
      ), [
        t, fetching, columns, data, pagination,
        filterList, handleFilterConfirm, handleDataStateChange, handleDetails,
      ])}
    </Container>
  );
}
